/**
 * Growable little-endian writer for the original ROSE binary formats.
 *
 * ROSE strings in the map formats are byte strings, not UTF-8. Rejecting
 * characters outside Latin-1 keeps length prefixes and offsets exact instead
 * of silently emitting a different number of bytes.
 */
export class BinaryWriter {
  private buffer: ArrayBuffer;
  private view: DataView;
  private bytes: Uint8Array;
  public pos = 0;

  constructor(initialCapacity = 256) {
    this.buffer = new ArrayBuffer(Math.max(1, initialCapacity));
    this.view = new DataView(this.buffer);
    this.bytes = new Uint8Array(this.buffer);
  }

  private ensure(byteCount: number): void {
    const required = this.pos + byteCount;
    if (required <= this.buffer.byteLength) return;

    let capacity = this.buffer.byteLength;
    while (capacity < required) capacity *= 2;
    const next = new ArrayBuffer(capacity);
    new Uint8Array(next).set(this.bytes.subarray(0, this.pos));
    this.buffer = next;
    this.view = new DataView(next);
    this.bytes = new Uint8Array(next);
  }

  writeUint8(value: number): void {
    this.ensure(1);
    this.view.setUint8(this.pos, value);
    this.pos += 1;
  }

  writeUint16(value: number): void {
    this.ensure(2);
    this.view.setUint16(this.pos, value, true);
    this.pos += 2;
  }

  writeUint32(value: number): void {
    this.ensure(4);
    this.view.setUint32(this.pos, value >>> 0, true);
    this.pos += 4;
  }

  writeInt32(value: number): void {
    this.ensure(4);
    this.view.setInt32(this.pos, value | 0, true);
    this.pos += 4;
  }

  writeFloat(value: number): void {
    this.ensure(4);
    this.view.setFloat32(this.pos, value, true);
    this.pos += 4;
  }

  writeBytes(value: ArrayLike<number>): void {
    this.ensure(value.length);
    this.bytes.set(value as any, this.pos);
    this.pos += value.length;
  }

  writeUint8Str(value: string): void {
    const encoded = BinaryWriter.encodeByteString(value);
    if (encoded.length > 0xff) {
      throw new Error(`ROSE uint8 string is too long (${encoded.length} bytes)`);
    }
    this.writeUint8(encoded.length);
    this.writeBytes(encoded);
  }

  writeUint16Str(value: string): void {
    const encoded = BinaryWriter.encodeByteString(value);
    if (encoded.length > 0xffff) {
      throw new Error(`ROSE uint16 string is too long (${encoded.length} bytes)`);
    }
    this.writeUint16(encoded.length);
    this.writeBytes(encoded);
  }

  writeIntVector2(value: { x: number; y: number }): void {
    this.writeInt32(value.x);
    this.writeInt32(value.y);
  }

  writeVector3(value: { x: number; y: number; z: number }): void {
    this.writeFloat(value.x);
    this.writeFloat(value.y);
    this.writeFloat(value.z);
  }

  writeVector3xzy(value: { x: number; y: number; z: number }): void {
    this.writeFloat(value.x);
    this.writeFloat(value.z);
    this.writeFloat(value.y);
  }

  writeQuat(value: { x: number; y: number; z: number; w: number }): void {
    this.writeFloat(value.x);
    this.writeFloat(value.y);
    this.writeFloat(value.z);
    this.writeFloat(value.w);
  }

  reserveUint32(): number {
    const offset = this.pos;
    this.writeUint32(0);
    return offset;
  }

  patchUint32(offset: number, value: number): void {
    if (offset < 0 || offset + 4 > this.pos) {
      throw new RangeError(`Cannot patch uint32 outside written data at ${offset}`);
    }
    this.view.setUint32(offset, value >>> 0, true);
  }

  toUint8Array(): Uint8Array {
    return this.bytes.slice(0, this.pos);
  }

  toArrayBuffer(): ArrayBuffer {
    const bytes = this.toUint8Array();
    const output = new ArrayBuffer(bytes.byteLength);
    new Uint8Array(output).set(bytes);
    return output;
  }

  private static encodeByteString(value: string): Uint8Array {
    const bytes = new Uint8Array(value.length);
    for (let index = 0; index < value.length; index++) {
      const code = value.charCodeAt(index);
      if (code > 0xff) {
        throw new Error(
          `ROSE byte string contains a non-Latin-1 character at index ${index}`,
        );
      }
      bytes[index] = code;
    }
    return bytes;
  }
}
